import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Select,
  Space,
  Switch,
  Tag,
  Typography
} from 'antd'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { WorkspaceAgent } from 'src/api/facade/types'
import { FormActionsBar } from 'src/components'

const { Text } = Typography

/** Values submitted by the form, mapped to the Squad write serializer upstream. */
export type RegisterAgentSubmit = {
  name: string
  description?: string
  systemPrompt?: string
  restricted?: boolean
  /** Create-only: `personal` (any member) or `workspace` (admins). */
  tier?: 'personal' | 'workspace'
  toolExecutionMode?: string
  /** Set when the editor confirmed that their own triggers on this agent keep
   * running with their permissions after it is opened up. Audit metadata only. */
  runAsExposureAck?: boolean
}

type FormScalars = {
  name: string
  description?: string
  systemPrompt?: string
  restricted?: boolean
  tier?: 'personal' | 'workspace'
  toolExecutionMode?: string
}

const TOOL_EXECUTION_OPTIONS = ['ask', 'auto']

// Removable-tag editor for the CALLER'S OWN "Always approve" exceptions (the
// list is grown from chat, never typed here). Deliberately NOT a Form.Item:
// each removal calls the dedicated per-user revoke endpoint immediately
// rather than collecting edits into the form's submit payload — the backend
// has no agent-wide "set the whole list" write (a shared agent has no single
// list to overwrite).
const AutoApprovedToolsField = ({
  value,
  onRevoke,
  emptyLabel
}: {
  value: string[]
  onRevoke: (toolName: string) => Promise<void>
  emptyLabel: string
}) => {
  // Reset local (optimistic) state when the agent's real list changes —
  // during render, not an effect, per React's "adjusting state on prop
  // change" guidance (avoids the extra cascading-render commit an effect
  // would cause).
  const [tools, setTools] = useState(value)
  const [syncedValue, setSyncedValue] = useState(value)
  if (value !== syncedValue) {
    setSyncedValue(value)
    setTools(value)
  }

  const revoke = async (toolName: string) => {
    const previous = tools
    setTools(tools.filter((t) => t !== toolName)) // optimistic
    try {
      await onRevoke(toolName)
    } catch {
      setTools(previous)
    }
  }

  if (!tools.length) {
    return <Text type="secondary">{emptyLabel}</Text>
  }

  return (
    <Space size={[4, 8]} wrap>
      {tools.map((name) => (
        <Tag
          key={name}
          closable
          onClose={(e) => {
            e.preventDefault()
            void revoke(name)
          }}
        >
          {name}
        </Tag>
      ))}
    </Space>
  )
}

type Props = {
  mode: 'create' | 'edit'
  initial?: WorkspaceAgent | null
  /** Existing agent names (excluding this one) for uniqueness validation. */
  existingNames?: string[]
  /**
   * Whether the caller is a workspace admin. Members may create only `personal`
   * agents (no tier picker); admins choose personal vs workspace (org-shared).
   */
  isAdmin?: boolean
  submitting?: boolean
  onCancel: () => void
  onSubmit: (values: RegisterAgentSubmit) => void
  /** Revoke one of the caller's own "Always approve" exceptions. Omitted in
   * create mode — a brand-new agent has no approvals to revoke yet. */
  onRevokeAutoApprovedTool?: (toolName: string) => Promise<void>
}

/**
 * Agent identity form (create + edit). Connecting MCP tools and granting scopes
 * happen on the agent's detail page (Connections → Access tabs), because scopes
 * only matter once the matching tools are connected — so they live next to the
 * connections, not in this form.
 */
export const AgentForm = ({
  mode,
  initial,
  existingNames = [],
  isAdmin = false,
  submitting,
  onCancel,
  onSubmit,
  onRevokeAutoApprovedTool
}: Props) => {
  const { t } = useTranslation()
  const { modal } = AntdApp.useApp()
  const [form] = Form.useForm<FormScalars>()

  // Only admins may create a shared (workspace) agent; members are pinned to
  // personal, so the picker is shown at create time for admins only.
  const showTierPicker = mode === 'create' && isAdmin

  const takenNames = useMemo(
    () => new Set(existingNames.map((n) => n.trim().toLowerCase())),
    [existingNames]
  )

  const initialValues = useMemo<FormScalars>(
    () => ({
      name: initial?.name ?? '',
      description: initial?.description ?? '',
      systemPrompt: initial?.systemPrompt ?? '',
      restricted: initial?.restricted ?? false,
      tier: 'personal',
      toolExecutionMode: initial?.toolExecutionMode ?? 'ask'
    }),
    [initial]
  )

  const submit = async () => {
    let values: FormScalars
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    // Clearing `restricted` opens the agent to everyone its tier covers. If the
    // editor has their own unattended triggers on it, those keep running with
    // THEIR permissions while colleagues can now change what the agent does —
    // so make that trade explicit before saving.
    const exposure = initial?.runAsExposure
    const widening = (initial?.restricted ?? false) && !values.restricted

    if (widening && (exposure?.total ?? 0) > 0) {
      const confirmed = await new Promise<boolean>((resolve) => {
        modal.confirm({
          title: t('workspace.agents.runAs.shareWarning.title'),
          content: t('workspace.agents.runAs.shareWarning.body', {
            count: exposure?.total ?? 0,
            schedules: exposure?.schedules ?? 0,
            channels: exposure?.channels ?? 0,
            widgets: exposure?.widgets ?? 0
          }),
          okText: t('workspace.agents.runAs.shareWarning.confirm'),
          cancelText: t('workspace.agents.runAs.shareWarning.cancel'),
          onOk: () => resolve(true),
          onCancel: () => resolve(false)
        })
      })

      if (!confirmed) return

      onSubmit({
        name: values.name.trim(),
        description: values.description?.trim() || undefined,
        systemPrompt: values.systemPrompt?.trim() || undefined,
        restricted: values.restricted,
        toolExecutionMode: values.toolExecutionMode,
        runAsExposureAck: true
      })

      return
    }

    onSubmit({
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      systemPrompt: values.systemPrompt?.trim() || undefined,
      restricted: values.restricted,
      toolExecutionMode: values.toolExecutionMode,
      // Members can only ever create personal agents; admins pick.
      ...(mode === 'create'
        ? { tier: showTierPicker ? values.tier : 'personal' }
        : {})
    })
  }

  const descriptionLabel = (
    <span>
      {t('workspace.agents.form.description')}{' '}
      <Text type="secondary">
        ({t('workspace.agents.form.descriptionOptional')})
      </Text>
    </span>
  )

  return (
    <>
      <Card>
        {mode === 'create' ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('workspace.agents.form.registerNotice')}
          />
        ) : null}

        <Form<FormScalars>
          form={form}
          layout="vertical"
          requiredMark="optional"
          initialValues={initialValues}
        >
          <Form.Item
            name="name"
            label={t('workspace.agents.form.name')}
            rules={[
              {
                required: true,
                message: t('workspace.agents.form.nameRequired')
              },
              {
                validator: (_rule, value: string) =>
                  value && takenNames.has(value.trim().toLowerCase())
                    ? Promise.reject(
                        new Error(t('workspace.agents.form.nameDuplicate'))
                      )
                    : Promise.resolve()
              }
            ]}
          >
            <Input
              maxLength={120}
              showCount
              placeholder={t('workspace.agents.form.namePlaceholder')}
              autoComplete="off"
              style={{ maxWidth: 520 }}
            />
          </Form.Item>

          <Form.Item name="description" label={descriptionLabel}>
            <Input.TextArea
              rows={2}
              maxLength={500}
              showCount
              placeholder={t('workspace.agents.form.descriptionPlaceholder')}
              style={{ maxWidth: 520 }}
            />
          </Form.Item>

          <Form.Item
            name="systemPrompt"
            label={t('workspace.agents.form.systemPrompt')}
            extra={t('workspace.agents.form.systemPromptHint')}
          >
            <Input.TextArea
              rows={4}
              maxLength={4000}
              showCount
              placeholder={t('workspace.agents.form.systemPromptPlaceholder')}
              style={{ maxWidth: 520 }}
            />
          </Form.Item>

          {showTierPicker ? (
            <Form.Item
              name="tier"
              label={t('workspace.agents.form.tier')}
              extra={t('workspace.agents.form.tierHint')}
              style={{ maxWidth: 260 }}
            >
              <Select
                options={(['personal', 'workspace'] as const).map((value) => ({
                  value,
                  label: t(`workspace.agents.tier.${value}`, {
                    defaultValue: value
                  })
                }))}
              />
            </Form.Item>
          ) : null}

          <Form.Item
            name="restricted"
            label={t('workspace.agents.form.restricted')}
            extra={t('workspace.agents.form.restrictedHint')}
            valuePropName="checked"
            style={{ maxWidth: 520 }}
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="toolExecutionMode"
            label={t('workspace.agents.form.toolExecutionMode')}
            extra={t('workspace.agents.form.toolExecutionModeHint')}
            style={{ maxWidth: 360 }}
          >
            <Select
              options={TOOL_EXECUTION_OPTIONS.map((value) => ({
                value,
                label: t(`workspace.agents.toolExecution.${value}`, {
                  defaultValue: value
                })
              }))}
            />
          </Form.Item>

          {mode === 'edit' && onRevokeAutoApprovedTool ? (
            <Form.Item
              label={t('workspace.agents.form.autoApprovedTools')}
              extra={t('workspace.agents.form.autoApprovedToolsHint')}
            >
              <AutoApprovedToolsField
                value={initial?.autoApprovedTools ?? []}
                onRevoke={onRevokeAutoApprovedTool}
                emptyLabel={t('workspace.agents.form.autoApprovedToolsEmpty')}
              />
            </Form.Item>
          ) : null}
        </Form>
      </Card>

      <FormActionsBar
        start={
          <Button onClick={onCancel} disabled={submitting}>
            {t('workspace.agents.form.cancel')}
          </Button>
        }
      >
        <Button
          type="primary"
          loading={submitting}
          onClick={() => void submit()}
        >
          {t(
            mode === 'create'
              ? 'workspace.agents.form.submit'
              : 'workspace.agents.form.save'
          )}
        </Button>
      </FormActionsBar>
    </>
  )
}
